import { useEffect, useState } from "react";
import { DollarSign, TrendingDown, CalendarDays, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";

const fmtM = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(2)}B`;
  return `$${n.toFixed(1)}M`;
};

// Build a small {arrow, pctText, abs, isGood} delta object.
// `good` configures which direction is "good" — e.g. cap-space up is good, dead-cap up is bad.
const computeDelta = (current, previous, good) => {
  if (current == null || previous == null) return null;
  const c = Number(current);
  const p = Number(previous);
  if (Number.isNaN(c) || Number.isNaN(p) || p === 0) return null;
  const diff = c - p;
  if (Math.abs(diff) < 0.05) return null; // ignore noise
  const pct = (diff / Math.abs(p)) * 100;
  const direction = diff > 0 ? "up" : "down";
  const isGood = good === "up" ? diff > 0 : diff < 0;
  return {
    direction,
    isGood,
    pctText: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`,
    absText: `${diff >= 0 ? "+" : "-"}$${Math.abs(diff).toFixed(1)}M`,
  };
};

const Delta = ({ delta, size = "sm" }) => {
  if (!delta) return null;
  const color = delta.isGood ? "text-emerald-400" : "text-titans-red";
  const Icon = delta.direction === "up" ? ArrowUp : ArrowDown;
  const iconPx = size === "lg" ? 12 : 10;
  const textCls = size === "lg" ? "text-[11px]" : "text-[10px]";
  return (
    <span className={`inline-flex items-center gap-0.5 ${color} ${textCls} font-cond uppercase tracking-wider`}>
      <Icon size={iconPx} />
      {delta.pctText}
    </span>
  );
};

export default function CapCorner({ variant = "card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cap-corner");
        setData(data);
      } catch (err) {
        // Widget hides silently on fetch error — log only in dev.
        if (process.env.NODE_ENV === "development") console.error("[CapCorner]", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  const isEmpty =
    !data ||
    [data.cap_space, data.dead_cap, data.committed_2026, data.committed_2027].every(
      (v) => v == null || v === ""
    );
  if (isEmpty) return null;

  const prev = data.previous || {};
  const stats = [
    {
      key: "cap_space",
      label: "Cap Space",
      value: data.cap_space,
      icon: DollarSign,
      accent: "text-emerald-400",
      delta: computeDelta(data.cap_space, prev.cap_space, "up"),
    },
    {
      key: "dead_cap",
      label: "Dead Cap",
      value: data.dead_cap,
      icon: TrendingDown,
      accent: "text-titans-red",
      delta: computeDelta(data.dead_cap, prev.dead_cap, "down"),
    },
    {
      key: "committed_2026",
      label: "2026 Committed",
      value: data.committed_2026,
      icon: CalendarDays,
      accent: "text-titans-blue",
      delta: computeDelta(data.committed_2026, prev.committed_2026, "down"),
    },
    {
      key: "committed_2027",
      label: "2027 Committed",
      value: data.committed_2027,
      icon: CalendarDays,
      accent: "text-titans-blue",
      delta: computeDelta(data.committed_2027, prev.committed_2027, "down"),
    },
  ];

  if (variant === "wide") {
    return (
      <div className="titans-card p-6" data-testid="cap-corner-wide">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">Cap Central</div>
            <div className="font-display text-3xl uppercase mt-1 leading-none">Salary Cap Snapshot</div>
          </div>
          {data.updated_at && (
            <div className="text-titans-muted text-[10px] font-cond uppercase tracking-wider">
              Updated {new Date(data.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div
              key={s.key}
              data-testid={`cap-stat-${s.key}`}
              className="bg-titans-void border border-[rgba(138,141,143,0.25)] p-4"
            >
              <div className={`flex items-center gap-1.5 ${s.accent} mb-2`}>
                <s.icon size={12} />
                <span className="font-cond uppercase tracking-wider text-[10px]">{s.label}</span>
              </div>
              <div className="font-display text-2xl md:text-3xl text-white leading-none">{fmtM(s.value)}</div>
              {s.delta && (
                <div className="mt-1.5" data-testid={`cap-delta-${s.key}`}>
                  <Delta delta={s.delta} size="lg" />
                </div>
              )}
            </div>
          ))}
        </div>
        {data.note && (
          <div className="text-titans-silver text-xs font-body italic mt-4">{data.note}</div>
        )}
      </div>
    );
  }

  // Sidebar / compact card variant
  return (
    <div className="titans-card p-5" data-testid="cap-corner-widget">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">Cap Central</div>
          <div className="font-display text-xl uppercase mt-1 leading-none">Salary Cap</div>
        </div>
        {data.updated_at && (
          <div className="text-titans-muted text-[10px] font-cond uppercase tracking-wider">
            {new Date(data.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
      <div className="space-y-2.5">
        {stats.map((s) => (
          <div
            key={s.key}
            data-testid={`cap-stat-${s.key}`}
            className="flex items-center justify-between border-l-2 border-[rgba(74,144,226,0.4)] pl-3 py-1"
          >
            <div className="flex items-center gap-1.5">
              <s.icon size={11} className={s.accent} />
              <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">{s.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {s.delta && <span data-testid={`cap-delta-${s.key}`}><Delta delta={s.delta} /></span>}
              <span className="font-display text-base text-white leading-none">{fmtM(s.value)}</span>
            </div>
          </div>
        ))}
      </div>
      {data.note && (
        <div className="text-titans-silver text-[11px] font-body italic mt-3 border-t border-[rgba(138,141,143,0.2)] pt-2">
          {data.note}
        </div>
      )}
    </div>
  );
}
